import * as React from 'react';

import {Routing} from 'nutshell-core/routing';

import {colors} from 'shells/colors/colors';
import {WebFormIcon} from 'shells/icon/svg/web-form-icon';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';
import {StatusBadge} from 'shells/status-badge/status-badge';
import {formatRelativeTimestamp} from 'shells/timestamp/format-relative-timestamp';

import type {
    GetWfFormsChat_forms_edges_node as Form,
    GetSchedulerMeetingsChat_meetings as Meeting,
} from 'nutshell-graphql-types';

import {ACTIVITY_TYPE_ICONS} from '../../../../setup/activity-types/consts';

export const getStatusBadge = (isConfigured: boolean) => {
    return (
        <StatusBadge
            level={isConfigured ? 'success' : 'info--grey'}
            text={isConfigured ? 'Configured' : 'Not configured'}
        />
    );
};

const DIVIDER = ' • ';

export const getFormOptions = (forms: Form[]): TwoLineOptionWithIcon<Form>[] => {
    return forms.map((form) => {
        const relative = formatRelativeTimestamp({
            datetime: form.lastSubmittedTime,
            format: 'fromNow',
        });
        const sublabel = `${relative ? `last submitted ${relative}` : 'No submissions'}`;

        return {
            value: form.publicFormId,
            label: form.name,
            sublabel: sublabel,
            // @ts-expect-error
            iconNode: <WebFormIcon size={19} fill={colors.grey} wrapWithDiv={false} />,
        };
    });
};

export const getFormOptionsWithDefault = (
    forms: Form[],
    defaultForm?: Form | null
): TwoLineOptionWithIcon<Form>[] => {
    const regularFormOptions = getFormOptions(forms);

    if (!defaultForm) {
        return regularFormOptions;
    }

    // Check if default form is already in the list
    const isAlreadyIncluded = regularFormOptions.some(
        (option) => option.value === defaultForm.publicFormId
    );

    if (isAlreadyIncluded) {
        return regularFormOptions;
    }

    // Add default form at the beginning
    const relative = formatRelativeTimestamp({
        datetime: defaultForm.lastSubmittedTime,
        format: 'fromNow',
    });
    const sublabel = `${relative ? `last submitted ${relative}` : 'No submissions'}`;

    const defaultOption = {
        value: defaultForm.publicFormId,
        label: `${defaultForm.name} (Default)`,
        sublabel: sublabel,
        // @ts-expect-error
        iconNode: <WebFormIcon size={19} fill={colors.grey} wrapWithDiv={false} />,
    } as const;

    return [defaultOption, ...regularFormOptions];
};

export const getSchedulerMeetingOptions = (
    meetings: Meeting[]
): TwoLineOptionWithIcon<Meeting>[] => {
    return meetings.map((meeting) => {
        const isBookable = meeting.validity ? meeting.validity.isBookable === true : true;
        const relative = formatRelativeTimestamp({
            datetime: meeting.lastBookedTime,
            format: 'fromNow',
        });
        let sublabel = `${meeting.primaryHost ? meeting.primaryHost.name : ''} ${DIVIDER}${
            meeting.durationMinutes
        } minutes${DIVIDER}${relative ? `last booked ${relative}` : 'No bookings'}`;

        if (!isBookable) {
            sublabel = `${sublabel}${DIVIDER}Not bookable`;
        }

        const Icon = ACTIVITY_TYPE_ICONS[meeting.activityType.icon];

        return {
            value: meeting.id,
            label: meeting.name,
            sublabel: sublabel,
            iconNode: <Icon size={19} fill={colors.grey} wrapWithDiv={false} />,
            isDisabled: !isBookable,
        };
    });
};

/**
 * Builds a URL to the leads list page filtered by chat and chatbot origins.
 * This includes leads created directly from chat threads (chat origin) as well as
 * leads created via form submissions during chatbot conversations (chatbot super origin).
 */
export function buildChatLeadsUrl(
    chatOrigin?: string | null,
    chatbotOrigin?: string | null
): string {
    // Filter out any null/undefined origins
    const originData = [chatOrigin, chatbotOrigin]
        .filter(Boolean)
        .map((origin) => ({data: origin}));

    const urlParams = {
        fields: {
            leads: 'description,createdTime,stagesets,owner,origin,creator',
        },
        filter: {
            origin: {
                anyAll: 'any',
                data: originData,
            },
        },
        sort: '-createdTime',
        view: 'list',
    } as const;

    const params = Routing.param(urlParams);

    return `/leads?${params}`;
}
